import { predictApi } from '@/api/predict.api';
import {
  DRILL_SIDE_VALUES,
  type DrillSide,
  HOLE_BOTTOM_VALUES,
  type HoleBottom,
  type HoleRequest,
  type HoleWCS,
  type ToolChangeInfo,
} from '@/types/predict.types';
import { Loader2, Maximize, Minimize } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface PredictToolsDialogProps {
  visible: boolean;
  onHide: () => void;
}

const DEFAULT_HOLE: HoleWCS = {
  x_mm: 198,
  y_mm: 60,
  diameter_mm: 34,
  depth_mm: 12,
  z_entry_mm: 5,
  bottom: 'DRILL_TIP',
  color_wall: null,
  color_bottom: null,
  color_chamfer: null,
  chamfer_angle_deg: null,
  chamfer_diameter_mm: null,
  chamfer_depth_mm: null,
  drill_point_angle_deg: null,
  thread_pitch_mm: null,
  tol: {
    diameter: { pm: 0 },
    position_xy: { pm: 0 },
    depth: { pm: 0 },
  },
};

const DEFAULT_FORM: HoleRequest = {
  hole: DEFAULT_HOLE,
  drill_side: '-Z',
  thickness: 0,
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">{children}</p>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
      {children}
      {required && ' *'}
    </label>
  );
}

function NumField({
  label,
  value,
  onChange,
  required,
  fractions = 3,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  required?: boolean;
  fractions?: number;
}) {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel required={required}>{label}</FieldLabel>
      <InputNumber
        value={value ?? null}
        onValueChange={(e) => onChange(e.value ?? null)}
        className="w-full"
        mode="decimal"
        minFractionDigits={0}
        maxFractionDigits={fractions}
        showButtons={false}
        placeholder="—"
      />
    </div>
  );
}

export const PredictToolsDialog: FC<PredictToolsDialogProps> = ({ visible, onHide }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<HoleRequest>(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ToolChangeInfo[] | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const setHole = (patch: Partial<HoleWCS>) => {
    setForm((prev) => ({ ...prev, hole: { ...prev.hole, ...patch } }));
  };

  const setTolDiameterPm = (pm: number | null) => {
    setForm((prev) => ({
      ...prev,
      hole: {
        ...prev.hole,
        tol: { ...prev.hole.tol, diameter: pm !== null ? { pm } : null },
      },
    }));
  };

  const setTolPositionXyPm = (pm: number | null) => {
    setForm((prev) => ({
      ...prev,
      hole: {
        ...prev.hole,
        tol: { ...prev.hole.tol, position_xy: pm !== null ? { pm } : null },
      },
    }));
  };

  const setTolDepthPm = (pm: number | null) => {
    setForm((prev) => ({
      ...prev,
      hole: {
        ...prev.hole,
        tol: { ...prev.hole.tol, depth: pm !== null ? { pm } : null },
      },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setResult(null);
    try {
      const tools = await predictApi.getToolsForHole(form);
      setResult(tools);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleHide = () => {
    setResult(null);
    onHide();
  };

  const dialogHeader = (
    <div className="flex items-center justify-between w-full pr-2">
      <span className="font-semibold text-lg">{t('toolManagement.predictToolsAriaLabel')}</span>
      <Button
        icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        onClick={() => setIsFullscreen((prev) => !prev)}
        text={true}
        rounded={true}
        size="small"
        pt={{
          root: {
            style: { outline: 'none', boxShadow: 'none' },
            className: 'focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none',
          },
        }}
      />
    </div>
  );

  const tolDiameterPm = form.hole.tol?.diameter && 'pm' in form.hole.tol.diameter ? form.hole.tol.diameter.pm : null;
  const tolPositionXyPm = form.hole.tol?.position_xy?.pm ?? null;
  const tolDepthPm = form.hole.tol?.depth && 'pm' in form.hole.tol.depth ? form.hole.tol.depth.pm : null;

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={handleHide}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-3xl'}`}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        root: {
          className: `border border-gray-200 rounded-lg shadow-lg dark:border-gray-700 max-h-[90%] transform scale-100 m-0 ${isFullscreen ? 'h-full fixed inset-0' : ''}`,
        },
        header: {
          className:
            'flex items-center justify-between shrink-0 bg-white text-gray-800 border-b border-gray-200 rounded-tl-lg rounded-tr-lg p-6 dark:bg-gray-900 dark:text-white/80 dark:border-gray-700',
        },
        content: {
          className: `overflow-y-auto bg-white text-gray-700 px-6 py-4 dark:bg-gray-900 dark:text-white/80 ${isFullscreen ? 'h-full flex flex-col' : ''}`,
        },
        footer: {
          className:
            'flex gap-2 shrink-0 justify-end align-center border-t border-gray-200 bg-white text-gray-700 px-6 pb-6 text-right rounded-b-lg dark:bg-gray-900 dark:text-white/80 dark:border-gray-700',
        },
      }}
      footer={
        <div className="flex gap-2 pt-4">
          <Button label={t('common.cancel')} onClick={handleHide} outlined={true} />
          <Button
            label={t('predictTools.predict')}
            onClick={handleSubmit}
            disabled={loading}
            icon={loading ? <Loader2 size={16} className="animate-spin" /> : undefined}
            iconPos="left"
          />
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* ── Hole – základní rozměry ── */}
        <div>
          <SectionTitle>{t('predictTools.sectionDimensions')}</SectionTitle>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
            <NumField label={t('predictTools.xMm')} value={form.hole.x_mm} onChange={(v) => setHole({ x_mm: v ?? 0 })} required={true} />
            <NumField label={t('predictTools.yMm')} value={form.hole.y_mm} onChange={(v) => setHole({ y_mm: v ?? 0 })} required={true} />
            <NumField
              label={t('predictTools.zEntryMm')}
              value={form.hole.z_entry_mm}
              onChange={(v) => setHole({ z_entry_mm: v ?? 0 })}
              required={true}
            />
            <NumField
              label={t('predictTools.diameterMm')}
              value={form.hole.diameter_mm}
              onChange={(v) => setHole({ diameter_mm: v ?? 0 })}
              required={true}
            />
            <NumField
              label={t('predictTools.depthMm')}
              value={form.hole.depth_mm}
              onChange={(v) => setHole({ depth_mm: v ?? 0 })}
              required={true}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800" />

        {/* ── Hole – vlastnosti ── */}
        <div>
          <SectionTitle>{t('predictTools.sectionProperties')}</SectionTitle>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div className="flex flex-col gap-1">
              <FieldLabel required={true}>{t('predictTools.bottom')}</FieldLabel>
              <Dropdown
                value={form.hole.bottom}
                options={HOLE_BOTTOM_VALUES.map((v) => ({ label: v, value: v }))}
                onChange={(e) => setHole({ bottom: e.value as HoleBottom })}
                className="w-full"
              />
            </div>
            <NumField
              label={t('predictTools.drillPointAngleDeg')}
              value={form.hole.drill_point_angle_deg}
              onChange={(v) => setHole({ drill_point_angle_deg: v })}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800" />

        {/* ── Chamfer ── */}
        <div>
          <SectionTitle>{t('predictTools.sectionChamfer')}</SectionTitle>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
            <NumField
              label={t('predictTools.chamferAngleDeg')}
              value={form.hole.chamfer_angle_deg}
              onChange={(v) => setHole({ chamfer_angle_deg: v })}
            />
            <NumField
              label={t('predictTools.chamferDiameterMm')}
              value={form.hole.chamfer_diameter_mm}
              onChange={(v) => setHole({ chamfer_diameter_mm: v })}
            />
            <NumField
              label={t('predictTools.chamferDepthMm')}
              value={form.hole.chamfer_depth_mm}
              onChange={(v) => setHole({ chamfer_depth_mm: v })}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800" />

        {/* ── Thread ── */}
        <div>
          <SectionTitle>{t('predictTools.sectionThread')}</SectionTitle>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
            <NumField
              label={t('predictTools.threadPitchMm')}
              value={form.hole.thread_pitch_mm}
              onChange={(v) => setHole({ thread_pitch_mm: v })}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800" />

        {/* ── Tolerance ── */}
        <div>
          <SectionTitle>{t('predictTools.sectionTolerance')}</SectionTitle>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
            <NumField label={t('predictTools.tolDiameter')} value={tolDiameterPm} onChange={setTolDiameterPm} />
            <NumField label={t('predictTools.tolPositionXy')} value={tolPositionXyPm} onChange={setTolPositionXyPm} />
            <NumField label={t('predictTools.tolDepth')} value={tolDepthPm} onChange={setTolDepthPm} />
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800" />

        {/* ── Colors ── */}
        <div>
          <SectionTitle>{t('predictTools.sectionColors')}</SectionTitle>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
            <ColorField
              label={t('predictTools.colorWall')}
              value={form.hole.color_wall ?? null}
              onChange={(v) => setHole({ color_wall: v })}
            />
            <ColorField
              label={t('predictTools.colorBottom')}
              value={form.hole.color_bottom ?? null}
              onChange={(v) => setHole({ color_bottom: v })}
            />
            <ColorField
              label={t('predictTools.colorChamfer')}
              value={form.hole.color_chamfer ?? null}
              onChange={(v) => setHole({ color_chamfer: v })}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800" />

        {/* ── Drilling parameters ── */}
        <div>
          <SectionTitle>{t('predictTools.sectionDrilling')}</SectionTitle>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div className="flex flex-col gap-1">
              <FieldLabel required={true}>{t('predictTools.drillSide')}</FieldLabel>
              <Dropdown
                value={form.drill_side}
                options={DRILL_SIDE_VALUES.map((v) => ({ label: v, value: v }))}
                onChange={(e) => setForm((prev) => ({ ...prev, drill_side: e.value as DrillSide }))}
                className="w-full"
              />
            </div>
            <NumField
              label={t('predictTools.thickness')}
              value={form.thickness}
              onChange={(v) => setForm((prev) => ({ ...prev, thickness: v ?? 0 }))}
              required={true}
            />
          </div>
        </div>

        {/* ── Result ── */}
        {result !== null && (
          <>
            <div className="border-t border-gray-100 dark:border-gray-800" />
            <div>
              <SectionTitle>
                {t('predictTools.resultCount', { count: result.length })}
              </SectionTitle>
              {result.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('predictTools.noToolsReturned')}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {result.map((tool, idx) => (
                    <div
                      key={`${tool.assembly_id}-${idx}`}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-gray-800 dark:text-white/90">
                          {idx + 1}. {tool.assembly_id}
                        </span>
                        <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded px-2 py-0.5">
                          {tool.tool_type}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                        <ResultRow label={t('predictTools.resToolName')} value={tool.tool_name} />
                        <ResultRow label={t('predictTools.resToolNumber')} value={tool.tool_number} />
                        <ResultRow label={t('predictTools.resToolDiameter')} value={tool.tool_diameter} unit="mm" />
                        <ResultRow label={t('predictTools.resToolLen')} value={tool.tool_len} unit="mm" />
                        <ResultRow label={t('predictTools.resToolOverallLen')} value={tool.tool_overall_len} unit="mm" />
                        <ResultRow label={t('predictTools.resAssemblyTlSetLen')} value={tool.assembly_tl_set_len} unit="mm" />
                        {tool.spindle !== null && <ResultRow label={t('predictTools.resSpindle')} value={tool.spindle} />}
                        {tool.feed_mach_value !== null && (
                          <ResultRow label={t('predictTools.resFeedMachValue')} value={tool.feed_mach_value} />
                        )}
                        {tool.tool_comment && <ResultRow label={t('predictTools.resComment')} value={tool.tool_comment} />}
                        <ResultRow label={t('predictTools.resSupportFinish')} value={String(tool.support_finish)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

const COLOR_OPTIONS: { label: string; value: string }[] = [
  { label: '#FFFFFF', value: '#FFFFFF' },
  { label: '#373737', value: '#373737' },
  { label: '#F4F400', value: '#F4F400' },
  { label: '#FFFF00', value: '#FFFF00' },
];

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const colorTemplate = (option: { label: string; value: string } | null) => {
    if (!option) return <span className="text-gray-400">—</span>;
    return (
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-4 h-4 rounded-sm border border-gray-300 dark:border-gray-600 shrink-0"
          style={{ backgroundColor: option.value }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <Dropdown
        value={value}
        options={COLOR_OPTIONS}
        onChange={(e) => onChange((e.value as string | null) ?? null)}
        placeholder="—"
        showClear={true}
        className="w-full"
        itemTemplate={colorTemplate}
        valueTemplate={(option) =>
          option ? colorTemplate(option as { label: string; value: string }) : <span className="text-gray-400">—</span>
        }
      />
    </div>
  );
}

function ResultRow({ label, value, unit }: { label: string; value: string | number; unit?: string }) {
  return (
    <div className="flex gap-1">
      <span className="text-gray-400 dark:text-gray-500 shrink-0">{label}:</span>
      <span className="font-medium text-gray-700 dark:text-gray-200">
        {value}
        {unit && <span className="text-gray-400 ml-0.5">{unit}</span>}
      </span>
    </div>
  );
}
